'use client';

import { ChevronDown, ChevronUp } from '@ps/web-fusion-icons';
import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import { NumberField as AriaNumberField, Group, Input, Label } from 'react-aria-components';
import type { NumberFieldProps as AriaNumberFieldProps } from 'react-aria-components';

import IconButton from '../IconButton/IconButton';
import { useFusionTheme } from '../hooks';

import styles from './NumberField.module.scss';

export interface NumberFieldProps
  extends Pick<
      AriaNumberFieldProps,
      | 'name'
      | 'aria-label'
      | 'defaultValue'
      | 'className'
      | 'formatOptions'
      | 'isDisabled'
      | 'autoFocus'
      | 'value'
      | 'defaultValue'
      | 'minValue'
      | 'maxValue'
      | 'step'
      | 'isReadOnly'
      | 'onFocus'
      | 'onBlur'
      | 'onChange'
    >,
    VariantProps<typeof numberFieldVariants> {
  placeholder?: string;
  label?: string;
  controls?: boolean;
}

const numberFieldVariants = cva(styles.container, {
  variants: {
    size: {
      medium: styles.sizeMedium,
      small: styles.sizeSmall
    }
  }
});

const NumberField = ({
  size = 'medium',
  formatOptions = { useGrouping: false },
  isReadOnly,
  placeholder,
  label,
  className,
  controls = true,
  ...props
}: NumberFieldProps) => {
  const { addThemeClassName } = useFusionTheme();
  const iconSize = size === 'small' ? 'sm' : 'md';

  return (
    <AriaNumberField
      className={addThemeClassName(numberFieldVariants({ size }), className)}
      {...props}
      formatOptions={formatOptions}
    >
      {label && <Label className={styles.label}>{label}</Label>}
      <Group className={styles.group}>
        <Input readOnly={isReadOnly} placeholder={placeholder} className={styles.input} />
        {controls && (
          <div className={styles.buttonGroup}>
            <IconButton
              excludeFromTabOrder={false}
              icon={ChevronUp}
              size={iconSize}
              slot="increment"
            />
            <IconButton
              excludeFromTabOrder={false}
              icon={ChevronDown}
              size={iconSize}
              slot="decrement"
            />
          </div>
        )}
      </Group>
    </AriaNumberField>
  );
};

export default NumberField;
